/**
 * Module 'term': interactive terminals (architecture doc 6.6, design doc 5.3, security 11.5).
 *
 * The machine agent owns one PTY per terminal. Three kinds:
 *   session  Claude Code running interactively inside a PTY the agent started (a session
 *            Orgus launched). People attach to it: watch, take the wheel, hand back.
 *   shell    a plain shell in a checkout. Admin only, and the node's ceiling can refuse it.
 *   sign-in  `claude auth login` in a launch account's config folder (ADR 0071): the person
 *            signs the folder in from the console. No session, no shell; writers (operator).
 * Watched sessions (started in a terminal or IDE) are never attached to (ADR 0001).
 *
 *   hub -> agent   ext term 'open' | 'data' | 'resize' | 'close' | 'replay'
 *   agent -> hub   ext term 'opened' | 'data' | 'exit' | 'list' | 'replay' | 'gap'
 *   browser <-> hub  WebSocket /api/term/:id (TermClientMessage / TermServerMessage)
 *   browser -> hub   POST /api/term (open), POST /api/term/:id/control (take, release, force)
 *
 * Every 'data', 'resize' and 'exit' frame carries a per-terminal sequence number that rises
 * by one, in each direction. Receivers use SeqReceiver: duplicates are dropped, early frames
 * wait for the missing one, and a gap that does not close asks for a resync (a replay of the
 * screen), never a silent skip.
 *
 * The hub records every terminal to <hub dir>/term/<id>.cast (asciicast v2: output, input,
 * resizes, and a marker for every change of control) and audits open, close and control.
 */
import type { Id, Ts, UiErrorShape } from '../index.ts';

export const TERM_EXT = 'term';

export type TermKind = 'session' | 'shell' | 'sign-in';
/** How a terminal is opened: a new shell, attach to a launched session, launch one in a PTY, or sign an account's folder in. */
export type TermOpenMode = 'shell' | 'attach' | 'launch' | 'sign-in';
/** 'pty': a real pseudo terminal (ConPTY on Windows). 'pipe': plain pipes, no resize, no full-screen apps. */
export type TermBackend = 'pty' | 'pipe';
export type TermStateName = 'starting' | 'live' | 'exited' | 'disconnected';

export type TermRole = 'viewer' | 'operator' | 'admin' | 'owner';
const ROLE_RANK: Record<TermRole, number> = { viewer: 0, operator: 1, admin: 2, owner: 3 };

export function termRoleAtLeast(role: string, need: TermRole): boolean {
  const have = ROLE_RANK[role as TermRole];
  return have !== undefined && have >= ROLE_RANK[need];
}

/** The role a terminal needs, to open, watch, control or close it. Shells are admin only. */
export function termRoleFor(kindOrMode: TermKind | TermOpenMode): TermRole {
  return kindOrMode === 'shell' ? 'admin' : 'operator';
}

/** Taking control away from someone else who holds it. */
export const TERM_FORCE_ROLE: TermRole = 'admin';

/** A terminal id: trm_ plus a ulid. Both ends check it; the hub uses it in a file name. */
export const TERM_ID_RE = /^trm_[A-Za-z0-9]{6,40}$/;

/**
 * Claude Code permission modes a terminal launch may ask for, and the role each needs.
 * bypassPermissions runs anything unattended, as much as a shell, so it is admin only.
 */
export const TERM_PERMISSION_MODES: Record<string, TermRole> = {
  default: 'operator',
  acceptEdits: 'operator',
  plan: 'operator',
  bypassPermissions: 'admin',
};

/** A model name or alias for --model: a word, never something that reads as an option. */
export const TERM_MODEL_RE = /^[A-Za-z][A-Za-z0-9._:-]{0,79}(?:\[1m\])?$/;

/**
 * The role a launch needs beyond operator: a raw folder instead of a known checkout, or a
 * permission mode that skips the prompts, needs admin. Null when the mode is unknown.
 */
export function termLaunchRole(req: { checkoutId?: unknown; cwd?: unknown; permissionMode?: unknown }): TermRole | null {
  let need: TermRole = 'operator';
  const hasCheckout = typeof req.checkoutId === 'string' && !!req.checkoutId;
  if (!hasCheckout && typeof req.cwd === 'string' && req.cwd.trim()) need = 'admin';
  if (typeof req.permissionMode === 'string' && req.permissionMode) {
    const mode = Object.prototype.hasOwnProperty.call(TERM_PERMISSION_MODES, req.permissionMode) ? TERM_PERMISSION_MODES[req.permissionMode] : undefined;
    if (!mode) return null;
    if (ROLE_RANK[mode] > ROLE_RANK[need]) need = mode;
  }
  return need;
}

/**
 * Node-side ceiling names this module honours, in <ORGUS_HOME>/ceilings.json `deny`
 * (the node trust file). 'term.attach' also covers launching a session in a PTY.
 * 'term.sign-in' is the sign-in terminal (ADR 0071): it runs only `claude auth login` in an
 * allowed account folder, never a shell or a session, so the default ceiling allows it.
 */
export const TERM_CEILING_CAPABILITIES = ['term.shell', 'term.attach', 'term.sign-in'] as const;

/** The ceiling entry that refuses this terminal, or null when the ceiling allows it. */
export function termCeilingHit(deny: readonly string[], mode: TermOpenMode): string | null {
  if (deny.includes('*')) return '*';
  if (deny.includes(`ext:${TERM_EXT}`)) return `ext:${TERM_EXT}`;
  const own = mode === 'shell' ? 'term.shell' : mode === 'sign-in' ? 'term.sign-in' : 'term.attach';
  if (deny.includes(own)) return own;
  if (mode === 'launch') {
    if (deny.includes('session.start')) return 'session.start';
    if (deny.includes('session.start:terminal')) return 'session.start:terminal';
  }
  return null;
}

export const TERM_LIMITS = {
  /** Output kept for a new viewer (hub) and for a resync after a gap (agent). */
  replayBytes: 256 * 1024,
  /** A writer who types nothing for this long loses control. */
  lockIdleMs: 10 * 60_000,
  /** A writer whose last window on the terminal closed keeps control this long. */
  writerGraceMs: 15_000,
  /** Out-of-order frames held while waiting for a missing one. */
  reorderWindow: 64,
  /** A missing frame not filled within this long triggers a resync. */
  gapTimeoutMs: 2_000,
  maxTermsPerMachine: 16,
  maxViewersPerTerm: 16,
  /** Terminals side by side in the split view. */
  maxSplit: 4,
  /** One input frame from a browser. */
  maxInputChars: 64 * 1024,
  /** A recording stops growing here; a marker says so. */
  maxCastBytes: 64 * 1024 * 1024,
  /** RN-1: recordings one machine may have on the hub (files and bytes); past either, new terminals are not recorded. */
  maxRecordingsPerMachine: 200,
  maxRecordingBytesPerMachine: 1024 * 1024 * 1024,
  minCols: 20,
  maxCols: 500,
  minRows: 5,
  maxRows: 200,
} as const;

export function clampSize(cols: unknown, rows: unknown): { cols: number; rows: number } {
  const n = (v: unknown, def: number, lo: number, hi: number) => {
    const x = typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : def;
    return Math.min(hi, Math.max(lo, x));
  };
  return { cols: n(cols, 120, TERM_LIMITS.minCols, TERM_LIMITS.maxCols), rows: n(rows, 32, TERM_LIMITS.minRows, TERM_LIMITS.maxRows) };
}

/** One terminal, as the agent reports it. */
export interface TermInfo {
  /** trm_<ulid>, chosen by the hub. */
  id: Id;
  machineId: Id;
  kind: TermKind;
  backend: TermBackend;
  cwd: string;
  /** "Claude Code: fix the flaky test" or "Shell: dev-portal". */
  title: string;
  /** The Claude Code session id, for kind 'session'. */
  sessionId: Id | null;
  model: string | null;
  pid: number | null;
  cols: number;
  rows: number;
  state: TermStateName;
  startedAt: Ts;
  endedAt: Ts | null;
  exitCode: number | null;
  /** Who opened it (actor id). */
  openedBy: string;
}

/** Exactly one writer at a time; everyone else watches. */
export interface TermLock {
  /** Actor id of the writer. */
  actor: string;
  /** Shown in the lock badge: "owner is typing". */
  label: string;
  since: Ts;
  lastInputAt: Ts;
}

/** A terminal in the hub: what the agent said, plus who is watching and who is typing. */
export interface TermSummary extends TermInfo {
  lock: TermLock | null;
  viewers: number;
  /** The recording, relative to the hub folder: term/<id>.cast. */
  recording: string;
}

/** GET /api/term: the terminals this caller may see (shells only for admins). */
export interface TermState {
  terms: TermSummary[];
}

/** One terminal in the state slice every signed-in client gets: no folder, title or people. */
export type TermBrief = Pick<TermSummary, 'id' | 'kind' | 'state' | 'machineId'>;

/**
 * state.ext.term and the SSE 'ext' term 'state' event, sent to every client whatever its role,
 * so it says only that something changed. Clients fetch GET /api/term for the details.
 */
export interface TermStateSlice {
  terms: TermBrief[];
}

// ---------------------------------------------------------------------------
// hub <-> agent frames (ExtMessage, ext 'term')
// ---------------------------------------------------------------------------

/** hub -> agent 'open'. The agent answers 'opened' with the same message id. */
export interface TermOpenFrame {
  termId: Id;
  mode: TermOpenMode;
  /** shell, launch: where. */
  cwd?: string;
  checkoutId?: Id;
  /** attach: which launched session. */
  sessionId?: Id;
  /** launch: the first prompt, and Claude Code options. */
  prompt?: string;
  model?: string;
  permissionMode?: string;
  /**
   * launch: the launch account the person chose (ADR 0071). The account itself comes in the
   * launch's grant; the agent refuses the launch when the grant does not name this one.
   */
  launchAccountId?: string;
  /** sign-in: the account and its config folder on this machine (the agent checks the folder, ADR 0071 d3). */
  signIn?: TermSignIn;
  cols: number;
  rows: number;
  /** Actor id, for the agent's own log. */
  actor: string;
}

/** TermOpenFrame.signIn: structured fields only; the agent maps them to CLAUDE_CONFIG_DIR. */
export interface TermSignIn {
  accountId: string;
  /** The account's name, for the terminal's title. */
  name: string;
  /** A folder sign-in (claude auth login in this folder). */
  configDir?: string;
  /**
   * A token sign-in (ADR 0088 amendment): claude setup-token in a scratch folder; the agent
   * captures the token it prints and sends it to the hub, which keeps it as the account's secret.
   */
  setupToken?: boolean;
}

export type TermRefusal = 'ceiling' | 'watched' | 'not-found' | 'no-pty' | 'bad-request' | 'limit' | 'failed';

/**
 * agent -> hub 'opened', answering 'open'. `seq` is the last output frame already sent and
 * `inSeq` the last hub frame applied, so a restarted hub continues both sequences.
 */
export type TermOpenedFrame =
  | { termId: Id; ok: true; info: TermInfo; seq: number; inSeq: number }
  | { termId: Id; ok: false; code: TermRefusal; error: string };

/** 'data' both ways: output (agent -> hub) or keystrokes (hub -> agent). */
export interface TermDataFrame {
  termId: Id;
  seq: number;
  data: string;
}

/** hub -> agent 'resize'. Shares the hub's sequence with 'data'. */
export interface TermResizeFrame {
  termId: Id;
  seq: number;
  cols: number;
  rows: number;
}

/** hub -> agent 'close': end the PTY (kills the shell or the session). */
export interface TermCloseFrame {
  termId: Id;
  reason?: string;
}

/** agent -> hub 'exit'. Shares the agent's output sequence. */
export interface TermExitFrame {
  termId: Id;
  seq: number;
  exitCode: number | null;
  endedAt: Ts;
}

/** agent -> hub 'list', on every (re)connect: what is still running here. */
export interface TermListFrame {
  terms: (TermInfo & { seq: number; inSeq: number })[];
}

/** hub -> agent 'replay': send the screen again (after a gap). agent -> hub 'replay': the screen. */
export interface TermReplayFrame {
  termId: Id;
  /** agent -> hub: the next output frame continues after this seq. */
  seq?: number;
  /** agent -> hub: the recent output, oldest first. */
  data?: string;
  /** hub -> agent: also make the program repaint (a resize nudge), for a full-screen program. */
  redraw?: boolean;
}

/** agent -> hub 'gap': keystrokes were lost between hub and agent. */
export interface TermGapFrame {
  termId: Id;
  expected: number;
  got: number;
}

// ---------------------------------------------------------------------------
// browser <-> hub
// ---------------------------------------------------------------------------

/** POST /api/term */
export interface TermOpenRequest {
  machineId: Id;
  mode: TermOpenMode;
  checkoutId?: Id;
  cwd?: string;
  sessionId?: Id;
  prompt?: string;
  model?: string;
  permissionMode?: string;
  /** launch: the account to run on, else the project default (ADR 0071). sign-in: the account to sign in. */
  launchAccountId?: string;
  cols?: number;
  rows?: number;
}

export interface TermOpenResponse {
  term: TermSummary;
  /** true when attach found a terminal that was already open. */
  existing: boolean;
}

/** POST /api/term/:id/control */
export interface TermControlRequest {
  /** take: become the writer when nobody is. release: hand back. force: take it from someone (admin). */
  action: 'take' | 'release' | 'force';
  /** take, force on a session: press Esc so Claude stops what it is doing (default true). */
  interrupt?: boolean;
  /** release on a session: typed into Claude Code as a message, so it knows what you changed. */
  note?: string;
  /** take, force: the writer's window size, applied to the PTY. */
  cols?: number;
  rows?: number;
}

/** Browser -> hub over the WebSocket. `seq` rises by one per input frame, per socket. */
export type TermClientMessage =
  | { t: 'in'; seq: number; data: string }
  | { t: 'resize'; cols: number; rows: number }
  | { t: 'ping' };

/** Hub -> browser over the WebSocket. Output 'data' carries the agent's sequence number. */
export type TermServerMessage =
  | { t: 'hello'; term: TermSummary; you: { actor: string; canWrite: boolean; canForce: boolean } }
  | { t: 'replay'; seq: number; data: string }
  | { t: 'data'; seq: number; data: string }
  | { t: 'term'; term: TermSummary }
  | { t: 'exit'; exitCode: number | null }
  | { t: 'error'; error: UiErrorShape }
  | { t: 'pong' };

/** WebSocket close codes the hub uses. */
export const TERM_WS_CLOSE = {
  notFound: 4404,
  forbidden: 4403,
  ended: 4000,
  tooMany: 4429,
  slow: 4008,
} as const;

/** Labels for the audit entries this module writes (their action names start with "term."). */
export const TERM_AUDIT_LABEL: Record<string, string> = {
  'term.open': 'Open terminal',
  'term.launch': 'Start session in a terminal',
  'term.attach': 'Attach to session',
  'term.close': 'Close terminal',
  'term.take': 'Take control of terminal',
  'term.release': 'Hand back terminal',
  'term.force': 'Take control from someone',
  'term.refused': 'Terminal refused',
  'term.recording.delete': 'Delete old terminal recordings',
  'term.cut': 'Terminal viewer cut off',
};

// ---------------------------------------------------------------------------
// Sequencing, shared by hub, agent and browser
// ---------------------------------------------------------------------------

export type SeqStatus = 'ok' | 'dup' | 'held' | 'overflow';

/**
 * In-order delivery of sequenced frames. Sequences start at 1 and rise by one.
 * push() returns the frames that can now be delivered, in order.
 *   dup       already delivered (a replayed frame): dropped
 *   held      arrived early: kept until the missing one comes
 *   overflow  too many held: the caller must resync (then call reset)
 */
export class SeqReceiver<T> {
  private nextSeq: number;
  private readonly held = new Map<number, T>();
  private readonly window: number;
  /** When the oldest held frame started waiting, for gap timeouts. */
  heldSince: number | null = null;

  constructor(start = 1, window: number = TERM_LIMITS.reorderWindow) {
    this.nextSeq = start;
    this.window = window;
  }

  get expected(): number {
    return this.nextSeq;
  }

  get waiting(): number {
    return this.held.size;
  }

  push(seq: number, item: T, now: number = Date.now()): { status: SeqStatus; items: T[] } {
    if (!Number.isInteger(seq) || seq < this.nextSeq || this.held.has(seq)) return { status: 'dup', items: [] };
    if (seq > this.nextSeq) {
      if (this.held.size >= this.window) return { status: 'overflow', items: [] };
      this.held.set(seq, item);
      if (this.heldSince === null) this.heldSince = now;
      return { status: 'held', items: [] };
    }
    const items = [item];
    this.nextSeq++;
    while (this.held.has(this.nextSeq)) {
      items.push(this.held.get(this.nextSeq) as T);
      this.held.delete(this.nextSeq);
      this.nextSeq++;
    }
    this.heldSince = this.held.size ? now : null;
    return { status: 'ok', items };
  }

  /** True when a frame has been missing for longer than `timeoutMs`. */
  stalled(now: number = Date.now(), timeoutMs: number = TERM_LIMITS.gapTimeoutMs): boolean {
    return this.heldSince !== null && now - this.heldSince > timeoutMs;
  }

  /** Give up on the missing frames and continue from the oldest held one. */
  skipGap(): { skippedTo: number | null; items: T[] } {
    if (!this.held.size) return { skippedTo: null, items: [] };
    const first = Math.min(...this.held.keys());
    return { skippedTo: first, items: this.reset(first - 1) };
  }

  /** Continue after `lastSeq` (a resync): held frames beyond it are kept, older ones dropped. */
  reset(lastSeq: number): T[] {
    this.nextSeq = lastSeq + 1;
    for (const k of [...this.held.keys()]) if (k < this.nextSeq) this.held.delete(k);
    const items: T[] = [];
    while (this.held.has(this.nextSeq)) {
      items.push(this.held.get(this.nextSeq) as T);
      this.held.delete(this.nextSeq);
      this.nextSeq++;
    }
    this.heldSince = this.held.size ? Date.now() : null;
    return items;
  }
}

/** Keep the last `max` characters of a growing text (the replay buffer). */
export function tailAppend(buf: string, add: string, max: number = TERM_LIMITS.replayBytes): string {
  const s = buf + add;
  return s.length > max ? s.slice(s.length - max) : s;
}

/** "owner is typing" */
export function lockLabel(lock: TermLock | null, you?: string): string {
  if (!lock) return 'Nobody is typing';
  return lock.actor === you ? 'You have control' : `${lock.label} is typing`;
}

/** Bracketed paste, so a multi-line note reaches Claude Code as one message. */
export function bracketedPaste(text: string): string {
  // No ESC at all inside the paste: then no marker in the text, however nested, can end it early.
  const clean = text.replace(/\u001b/g, '');
  return `\u001b[200~${clean}\u001b[201~`;
}
